import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ShoppingCart, Star, Search, Filter, Check, Utensils, Palette, BookOpen, Shirt, Leaf, Gift, ArrowRight } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { Link } from 'react-router-dom';
const fadeIn = {
  initial: {
    opacity: 0,
    y: 20
  },
  whileInView: {
    opacity: 1,
    y: 0
  },
  viewport: {
    once: true,
    margin: '-100px'
  },
  transition: {
    duration: 0.6
  }
};
export function Shop() {
  const {
    addToCart
  } = useCart();
  const [addedItems, setAddedItems] = useState<Record<number, boolean>>({});
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [maxPrice, setMaxPrice] = useState(100);
  const products = [{
    id: 1,
    name: 'Heritage Canvas Tote',
    price: '$25.00',
    numericPrice: 25,
    rating: 5,
    img: 'https://images.unsplash.com/photo-1544816155-12df9643f363?q=80&w=2787&auto=format&fit=crop',
    cat: 'Heritage Apparel'
  }, {
    id: 2,
    name: 'Wonder Boy Spice Blend',
    price: '$15.00',
    numericPrice: 15,
    rating: 5,
    img: 'https://images.unsplash.com/photo-1596040033229-a9821ebd058d?q=80&w=2940&auto=format&fit=crop',
    cat: 'Wonder Boy Restaurant Items'
  }, {
    id: 3,
    name: 'Handwoven Basket',
    price: '$45.00',
    numericPrice: 45,
    rating: 4,
    img: 'https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?q=80&w=2787&auto=format&fit=crop',
    cat: 'Cultural Crafts & Artwork'
  }, {
    id: 4,
    name: 'African History Book Set',
    price: '$60.00',
    numericPrice: 60,
    rating: 5,
    img: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?q=80&w=2787&auto=format&fit=crop',
    cat: 'Educational Materials'
  }, {
    id: 5,
    name: 'Poch Global T-Shirt',
    price: '$30.00',
    numericPrice: 30,
    rating: 5,
    img: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?q=80&w=2960&auto=format&fit=crop',
    cat: 'Heritage Apparel'
  }, {
    id: 6,
    name: 'Natural Shea Butter',
    price: '$20.00',
    numericPrice: 20,
    rating: 4,
    img: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?q=80&w=2853&auto=format&fit=crop',
    cat: 'Health & Wellness Products'
  }];
  const categories = ['Wonder Boy Restaurant Items', 'Cultural Crafts & Artwork', 'Educational Materials', 'Heritage Apparel', 'Health & Wellness Products', 'Gift Packages'];
  const toggleCategory = (cat: string) => {
    setSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]);
  };
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategories.length === 0 || selectedCategories.includes(product.cat);
    const matchesPrice = product.numericPrice <= maxPrice;
    return matchesSearch && matchesCategory && matchesPrice;
  });
  const handleAddToCart = (product: any) => {
    addToCart(product);
    setAddedItems(prev => ({
      ...prev,
      [product.id]: true
    }));
    setTimeout(() => {
      setAddedItems(prev => ({
        ...prev,
        [product.id]: false
      }));
    }, 2000);
  };
  return <div className="pt-0 bg-warmwhite" data-id="element-1203" data-mp="boemxqzqjuxi">
      {/* Hero Section */}
      <section className="relative pt-32 pb-16 bg-navy overflow-hidden" data-id="element-1204" data-mp="goaug07n64ht">
        <div className="absolute inset-0 z-0" data-id="element-1205" data-mp="2qcjl4sj4q72">
          <img src="https://images.unsplash.com/photo-1606760227091-3dd870d97f1d?q=80&w=2787&auto=format&fit=crop" alt="Shop" className="w-full h-full object-cover opacity-20" data-id="element-1206" data-mp="2pjk8ovwh2yy" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/80 to-transparent" data-id="element-1207" data-mp="2sf4zysa0802z" />
        </div>
        <div className="container mx-auto px-6 md:px-12 relative z-10 text-center max-w-4xl" data-id="element-1208" data-mp="37oi7svumyjle">
          <h1 className="text-5xl md:text-6xl font-serif font-bold text-white mb-6" data-id="element-1209" data-mp="32ybkr0frv759">
            Shop Poch Global
          </h1>
          <p className="text-xl text-white/80 leading-relaxed mb-8" data-id="element-1210" data-mp="2y00awpph8wn1">
            Support our mission while enjoying authentic African products and
            experiences. Every purchase makes an impact.
          </p>
          <button className="px-8 py-4 bg-gold hover:bg-gold-light text-white rounded-full font-bold transition-all shadow-lg flex items-center gap-2 mx-auto" data-id="element-1211" data-mp="3qn46y56le0mo">
            <ShoppingCart className="w-5 h-5" data-id="element-1212" data-mp="3hmg3u5iqlkok" /> Start Shopping
          </button>
        </div>
      </section>

      {/* Impact Banner */}
      <div className="bg-navy text-white py-4 text-center text-sm font-medium tracking-wide relative overflow-hidden" data-id="element-1213" data-mp="3cncxmhr0t8f0">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-1214" data-mp="265ps66vjo0nk" />
        <span className="relative z-10" data-id="element-1215" data-mp="gqnffu5eub5f">100% of proceeds support scholarships, community programs, and youth empowerment.</span>
      </div>

      {/* Product Categories Grid */}
      <section className="py-12 lg:py-16 bg-white border-b border-softgray" data-id="element-1216" data-mp="lnnli529fdwj">
        <div className="container mx-auto px-6 md:px-12" data-id="element-1217" data-mp="ppsindrx2snx">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-1218" data-mp="uilmlzfk9wyy">
            <h2 className="text-4xl font-serif font-bold text-navy mb-4" data-id="element-1219" data-mp="ujwp7vx9syoh">
              Product Categories
            </h2>
            <p className="text-mediumgray text-lg" data-id="element-1220" data-mp="14fzrr2w1azby">
              Browse our curated collections. Every purchase makes an impact.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1221" data-mp="19dhzwr289aiw">
            {[{
            title: 'Wonder Boy Restaurant Items',
            desc: 'African dishes for pickup/delivery',
            icon: Utensils
          }, {
            title: 'Cultural Crafts & Artwork',
            desc: 'Handmade African crafts',
            icon: Palette
          }, {
            title: 'Educational Materials',
            desc: 'Books, courses, resources',
            icon: BookOpen
          }, {
            title: 'Heritage Apparel',
            desc: 'Traditional & modern African clothing',
            icon: Shirt
          }, {
            title: 'Health & Wellness Products',
            desc: 'Natural African products',
            icon: Leaf
          }, {
            title: 'Gift Packages',
            desc: 'Curated gift sets',
            icon: Gift
          }].map((cat, i) => <motion.div key={i} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-warmwhite p-8 rounded-3xl shadow-sm border border-softgray hover:shadow-premium hover:border-gold/30 transition-all duration-300 group flex flex-col h-full cursor-pointer" onClick={() => {
            setSelectedCategories([cat.title]);
            window.scrollTo({
              top: 800,
              behavior: 'smooth'
            });
          }} data-id="element-1222" data-mp="1o364rhzhuxcy" data-mpi={i}>
                <div className="w-14 h-14 bg-white text-gold rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-sm" data-id="element-1223" data-mp="1xp7baslyijmh">
                  <cat.icon className="w-7 h-7" data-id="element-1224" data-mp="1oqltizob78mr" />
                </div>
                <div className="flex-grow" data-id="element-1225" data-mp="1a17m3x4an1a0">
                  <h3 className="text-xl font-bold text-navy mb-2 group-hover:text-gold transition-colors" data-id="element-1226" data-mp="1a28x61lb7nrq">
                    {cat.title}
                  </h3>
                  <p className="text-mediumgray mb-6" data-id="element-1227" data-mp="110rkyub0w68b">{cat.desc}</p>
                </div>
                <div className="text-navy font-semibold flex items-center gap-2 group-hover:gap-3 transition-all mt-auto" data-id="element-1228" data-mp="15zmdwp2pfb65">
                  Browse Items <ArrowRight className="w-4 h-4" data-id="element-1229" data-mp="1lhvo59byyfva" />
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Shop Content */}
      <section className="py-12 lg:py-16" data-id="element-1230" data-mp="1biwr6o0kg8o4">
        <div className="container mx-auto px-4 sm:px-6 md:px-12" data-id="element-1231" data-mp="1bhv75u1kobu9">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-12" data-id="element-1232" data-mp="17imaagb8jxuh">
            {/* Sidebar Filters */}
            <div className="w-full lg:w-1/4" data-id="element-1233" data-mp="12jt6xgpnxn81">
              <div className="bg-white p-5 sm:p-6 rounded-3xl shadow-sm border border-softgray lg:sticky lg:top-32" data-id="element-1234" data-mp="12kktk21a8am6">
                <h3 className="font-bold text-navy mb-6 flex items-center gap-2" data-id="element-1235" data-mp="ssouanngmegd">
                  <Filter className="w-5 h-5" data-id="element-1236" data-mp="sqlsl6cy2wvt" /> Filters
                </h3>

                <div className="mb-8" data-id="element-1237" data-mp="nvonubmp79oq">
                  <h4 className="font-semibold text-mediumgray mb-3 text-sm uppercase tracking-wider" data-id="element-1238" data-mp="js0djl6olzzs">
                    Categories
                  </h4>
                  <ul className="space-y-2" data-id="element-1239" data-mp="etyw14owm8qf">
                    {categories.map(cat => <li key={cat} data-id="element-1240" data-mp="2qg0u9legn4nu">
                        <label className="flex items-center gap-3 text-mediumgray hover:text-gold cursor-pointer" data-id="element-1241" data-mp="2vflpbkqiesva">
                          <input type="checkbox" checked={selectedCategories.includes(cat)} onChange={() => toggleCategory(cat)} className="rounded text-gold focus:ring-gold" data-id="element-1242" data-mp="2zgp7idw150zo" />{' '}
                          {cat}
                        </label>
                      </li>)}
                  </ul>
                </div>

                <div data-id="element-1243" data-mp="2lhfpr9s6dytl">
                  <h4 className="font-semibold text-mediumgray mb-3 text-sm uppercase tracking-wider flex justify-between" data-id="element-1244" data-mp="2lhzaey97n1bb">
                    <span data-id="element-1245" data-mp="1o1aa58zws741">Max Price</span>
                    <span className="text-navy" data-id="element-1246" data-mp="1o07j912l64o4">${maxPrice}</span>
                  </h4>
                  <input type="range" min="0" max="100" value={maxPrice} onChange={e => setMaxPrice(Number(e.target.value))} className="w-full accent-gold" data-id="element-1247" data-mp="1sz21o1wn5r7p" />
                  <div className="flex justify-between text-sm text-mediumgray mt-2" data-id="element-1248" data-mp="34vb44dqy3xtx">
                    <span data-id="element-1249" data-mp="39vyvj0j36goo">$0</span>
                    <span data-id="element-1250" data-mp="2c8qf3jxxzxtj">$100+</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Product Grid */}
            <div className="w-full lg:w-3/4" data-id="element-1251" data-mp="2h8l0x92v1x7q">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 lg:mb-8 gap-4" data-id="element-1252" data-mp="2m6wenuwa0rv8">
                <h2 className="text-2xl lg:text-3xl font-bold text-navy" data-id="element-1253" data-mp="2qy4k6fuhjt34">
                  {filteredProducts.length} Items Found
                </h2>
                <div className="relative w-full sm:w-auto" data-id="element-1254" data-mp="1tg4lz7ef6lcn">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-mediumgray w-4 h-4" data-id="element-1255" data-mp="1yga5u1eg73k6" />
                  <input type="text" placeholder="Search products..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full sm:w-auto pl-10 pr-4 py-2 bg-white border border-softgray rounded-full text-sm focus:outline-none focus:border-navy" data-id="element-1256" data-mp="1qwrd33fejvye" />
                </div>
              </div>

              {filteredProducts.length === 0 ? <div className="text-center py-12 lg:py-20 bg-white rounded-3xl border border-softgray" data-id="element-1257" data-mp="137yeez7eba03">
                  <p className="text-mediumgray text-base lg:text-lg" data-id="element-1258" data-mp="ya6qeg9vg816">
                    No products found matching your criteria.
                  </p>
                  <button onClick={() => {
                setSearchQuery('');
                setSelectedCategories([]);
                setMaxPrice(100);
              }} className="mt-4 text-gold font-semibold hover:underline" data-id="element-1259" data-mp="t87nt8lyhf5a">
                    Clear Filters
                  </button>
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1260" data-mp="nu13sa5ziqo">
                  {filteredProducts.map(product => <motion.div key={product.id} variants={fadeIn} initial="initial" whileInView="whileInView" className="bg-white rounded-2xl overflow-hidden shadow-sm border border-softgray hover:shadow-premium transition-all duration-300 group" data-id="element-1261" data-mp="5nggo8dgtb2n">
                    <div className="h-64 overflow-hidden relative bg-warmwhite" data-id="element-1262" data-mp="okzppbzxatp2">
                      <Link to={`/product/${product.id}`} data-id="element-1263" data-mp="1cb3rrexgqldy">
                        <img src={product.img} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 mix-blend-multiply" data-id="element-1264" data-mp="1c9ix7pb5f2g3" />
                      </Link>
                      <button onClick={() => handleAddToCart(product)} className={`absolute bottom-4 right-4 w-10 h-10 rounded-full shadow-md flex items-center justify-center transition-all duration-300 ${addedItems[product.id] ? 'bg-forest text-white opacity-100 translate-y-0' : 'bg-white text-navy hover:bg-gold hover:text-white opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0'}`} data-id="element-1265" data-mp="1mcy3kin9g54a">
                        {addedItems[product.id] ? <Check className="w-5 h-5" data-id="element-1266" data-mp="1cgupcef4wg0z" /> : <ShoppingCart className="w-5 h-5" data-id="element-1267" data-mp="1cgl8phfzbl7y" />}
                      </button>
                    </div>
                    <div className="p-5" data-id="element-1268" data-mp="2sttab9tuez18">
                      <div className="text-xs text-mediumgray font-medium uppercase tracking-wider mb-1" data-id="element-1269" data-mp="2swxnuxt7774k">
                        {product.cat}
                      </div>
                      <Link to={`/product/${product.id}`} data-id="element-1270" data-mp="xgh7hrnurba9">
                        <h3 className="font-bold text-navy mb-2 truncate hover:text-gold transition-colors" data-id="element-1271" data-mp="12d7iw1oppipc">
                          {product.name}
                        </h3>
                      </Link>
                      <div className="flex items-center justify-between" data-id="element-1272" data-mp="1bexivvwntpyq">
                        <span className="font-bold text-gold" data-id="element-1273" data-mp="1ge1sgnnhxppl">
                          {product.price}
                        </span>
                        <div className="flex text-amber-400" data-id="element-1274" data-mp="1lbk62rcgkt3x">
                          {[...Array(product.rating)].map((_, i) => <Star key={i} className="w-3 h-3 fill-current" data-id="element-1275" data-mp="1q5obv79zmv3f" data-mpi={i} />)}
                        </div>
                      </div>
                    </div>
                  </motion.div>)}
              </div>}
            </div>
          </div>
        </div>
      </section>

      {/* Impact Message */}
      <section className="py-12 lg:py-16 bg-navy text-white relative overflow-hidden" data-id="element-1276" data-mp="3omgiqxle1xn1">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')]" data-id="element-1277" data-mp="3jm11huxzk5zs" />
        <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-1278" data-mp="3fl8t13wxu54k">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-16" data-id="element-1279" data-mp="3ame37i0jzgay">
            <div className="lg:w-1/2 w-full order-2 lg:order-1" data-id="element-1280" data-mp="b70lw0fxmpsy">
              <h2 className="text-3xl text-white md:text-4xl font-serif font-bold mb-4 lg:mb-6" data-id="element-1281" data-mp="b8bovy1jztj0">Shop with Purpose</h2>
              <p className="text-base lg:text-lg text-white/80 leading-relaxed mb-8 lg:mb-10" data-id="element-1282" data-mp="2e6zzk5zcs64">
                Every purchase supports Poch Global's mission. A portion of proceeds goes directly to scholarships, community programs, and youth empowerment initiatives.
              </p>
              
              <div className="space-y-6" data-id="element-1283" data-mp="q3rkw8lp1zi1">
                <div data-id="element-1284" data-mp="q2zr93bnqbue">
                  <div className="flex justify-between text-sm font-bold mb-2" data-id="element-1285" data-mp="q2gkmirgj9vo">
                    <span data-id="element-1286" data-mp="191belbdqdvh0">Scholarships & Education</span>
                    <span className="text-gold" data-id="element-1287" data-mp="1dytn50nu68oh">40%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2" data-id="element-1288" data-mp="1e0xsln1dvfqv">
                    <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '40%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.2
                  }} className="bg-gold h-2 rounded-full" data-id="element-1289" data-mp="107g5xtdehmwm" />
                  </div>
                </div>
                <div data-id="element-1290" data-mp="h9vsdig36arb">
                  <div className="flex justify-between text-sm font-bold mb-2" data-id="element-1291" data-mp="haf0k7fxpks5">
                    <span data-id="element-1292" data-mp="lbgz0m211st2">Health Programs</span>
                    <span className="text-gold" data-id="element-1293" data-mp="lc8ulwvmjqjc">30%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2" data-id="element-1294" data-mp="2bnn18fqlcqlp">
                    <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '30%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.4
                  }} className="bg-gold h-2 rounded-full" data-id="element-1295" data-mp="2fn5jf3r12nku" />
                  </div>
                </div>
                <div data-id="element-1296" data-mp="8zod7z6kl50s">
                  <div className="flex justify-between text-sm font-bold mb-2" data-id="element-1297" data-mp="8z551a6q1uzy">
                    <span data-id="element-1298" data-mp="noksohayhkbf">Community Outreach</span>
                    <span className="text-gold" data-id="element-1299" data-mp="nn9i9yn09ebc">20%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2" data-id="element-1300" data-mp="iphoez2svi4i">
                    <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '20%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.6
                  }} className="bg-gold h-2 rounded-full" data-id="element-1301" data-mp="wq0vo3zfyk5m" />
                  </div>
                </div>
                <div data-id="element-1302" data-mp="28mcyi3gmga2a">
                  <div className="flex justify-between text-sm font-bold mb-2" data-id="element-1303" data-mp="28mwhlni0d4hw">
                    <span data-id="element-1304" data-mp="2cnyf9zwa18hr">Operations & Admin</span>
                    <span className="text-gold" data-id="element-1305" data-mp="2cmf1cfy3f9j5">10%</span>
                  </div>
                  <div className="w-full bg-white/10 rounded-full h-2" data-id="element-1306" data-mp="2hfpy1o96y9xc">
                    <motion.div initial={{
                    width: 0
                  }} whileInView={{
                    width: '10%'
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 1,
                    delay: 0.8
                  }} className="bg-gold h-2 rounded-full" data-id="element-1307" data-mp="2mdi19efadigu" />
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:w-1/2 w-full order-1 lg:order-2" data-id="element-1308" data-mp="23nta7wek2m6r">
              <div className="rounded-[2rem] overflow-hidden shadow-2xl border border-white/10 relative" data-id="element-1309" data-mp="23l73u03eicq4">
                <img src="https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=2940&auto=format&fit=crop" alt="Impact" className="w-full h-[300px] md:h-[400px] lg:h-[500px] object-cover" data-id="element-1310" data-mp="3a5q4vsn95x2u" />
                <div className="absolute inset-0 bg-navy/20 mix-blend-multiply" data-id="element-1311" data-mp="yg0heozab0wi" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>;
}